import { useEffect, useState } from 'react';

const PRELOAD_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
} as const;

type PreloadStatus = (typeof PRELOAD_STATUS)[keyof typeof PRELOAD_STATUS];

type UsePreloadImageOptions = {
  enabled: boolean;
  imageUrl: string;
};

const preloadStateByUrl = new Map<string, PreloadStatus>();
const listenersByUrl = new Map<string, Set<() => void>>();

const getPreloadStatus = (imageUrl: string): PreloadStatus =>
  preloadStateByUrl.get(imageUrl) ?? PRELOAD_STATUS.IDLE;

const notifyPreloadListeners = (imageUrl: string) => {
  listenersByUrl.get(imageUrl)?.forEach((listener) => listener());
};

export const startImagePreload = (imageUrl: string) => {
  const status = getPreloadStatus(imageUrl);

  if (status === PRELOAD_STATUS.READY || status === PRELOAD_STATUS.LOADING)
    return;

  preloadStateByUrl.set(imageUrl, PRELOAD_STATUS.LOADING);

  const img = new Image();

  const finish = () => {
    preloadStateByUrl.set(imageUrl, PRELOAD_STATUS.READY);
    notifyPreloadListeners(imageUrl);
  };

  img.onload = finish;
  img.onerror = finish;
  img.src = imageUrl;
};

export const usePreloadImage = ({
  enabled,
  imageUrl,
}: UsePreloadImageOptions) => {
  const [isReady, setIsReady] = useState(
    () => getPreloadStatus(imageUrl) === PRELOAD_STATUS.READY
  );

  useEffect(() => {
    if (!enabled) {
      setIsReady(false);
      return undefined;
    }

    if (getPreloadStatus(imageUrl) === PRELOAD_STATUS.READY) {
      setIsReady(true);
      return undefined;
    }

    startImagePreload(imageUrl);

    const handlePreloadUpdate = () => {
      if (getPreloadStatus(imageUrl) === PRELOAD_STATUS.READY) setIsReady(true);
    };

    const listeners = listenersByUrl.get(imageUrl) ?? new Set();
    listeners.add(handlePreloadUpdate);
    listenersByUrl.set(imageUrl, listeners);

    return () => {
      listeners.delete(handlePreloadUpdate);
    };
  }, [enabled, imageUrl]);

  return isReady;
};
